import {
  EnumProductEventMetadataTicketStructure,
  EnumProductTierSessionScope,
} from "gql/generated";
import type { CartItem } from "widgets/booker/contexts";

type EventTierOptionLike = {
  _id?: string | null;
  sessionScope?: EnumProductTierSessionScope | null;
  minSessions?: number | null;
  maxSessions?: number | null;
};

type EventProductLike = {
  _id: string;
  eventMetadata?: {
    ticketStructure?: EnumProductEventMetadataTicketStructure | null;
  } | null;
};

/**
 * Whether the buyer's event selection is fully specified and safe to send to
 * the API. Mirrors the backend planner's requirements so we don't enable
 * checkout for a selection the server will reject:
 *  - at least one ticket/pass selected (quantity > 0)
 *  - SINGLE-scope tiers have a session chosen
 *  - PICK_N tiers have exactly N eligible sessions chosen
 * SIMPLE / session-picker flows carry the session in the cart key, so a
 * positive quantity is sufficient.
 */
export const isEventSelectionComplete = (
  cart: CartItem[],
  product: EventProductLike | null | undefined,
  options: EventTierOptionLike[],
): boolean => {
  if (!product) return false;

  const items = cart.filter(
    item => item.productId.split("::")[0] === product._id,
  );
  const totalQuantity = items.reduce(
    (sum, item) => sum + (item.quantity ?? 0),
    0,
  );
  if (totalQuantity <= 0) return false;

  if (
    product.eventMetadata?.ticketStructure !==
    EnumProductEventMetadataTicketStructure.Packages
  ) {
    return true;
  }

  for (const item of items) {
    for (const guestTier of item.guestsPerTier ?? []) {
      if ((guestTier.count ?? 0) <= 0) continue;

      const option = options.find(o => o._id === guestTier.tierId);
      if (!option) return false;

      const scope = option.sessionScope ?? EnumProductTierSessionScope.Single;

      if (scope === EnumProductTierSessionScope.Single && !guestTier.sessionId) {
        return false;
      }

      if (scope === EnumProductTierSessionScope.PickN) {
        const required = option.minSessions ?? option.maxSessions ?? 0;
        const chosen = (guestTier.sessionIds ?? []).filter(Boolean);
        if (required <= 0 || chosen.length !== required) return false;
      }
    }
  }

  return true;
};
